import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, finalize, map, throwError } from 'rxjs';
import { API_PATHS, STORAGE_KEYS } from '../constants/api.constants';
import type {
  AuthRole,
  AuthUser,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
} from '../models/auth.model';
import { ApiService } from './api.service';
import { TokenService } from './token.service';
import { ToastService } from '../../shared/components/ui/toast/toast.service';

export type Portal = 'admin' | 'employee';

const ROLE_PORTAL_MAP: Record<string, Portal> = {
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
};

/**
 * Application-wide authentication + portal routing (admin vs employee UI).
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  private readonly token = signal<string | null>(this.tokenService.getToken());
  userState = signal<AuthUser | null>(this.readUser());
  private readonly activeRoleIdState = signal<number | null>(this.readActiveRoleId());

  readonly isAuthenticated = computed(() => !!this.token());
  readonly currentUser = this.userState.asReadonly();
  readonly userName = computed(() => this.userState()?.userName ?? null);
  readonly roles = computed(() => this.userState()?.roles ?? []);
  readonly delegators = computed(() => this.userState()?.delegators ?? []);
  readonly activeRole = computed(() => {
    const roles = this.roles();
    const activeId = this.activeRoleIdState();
    return roles.find((role) => role.id === activeId) ?? roles[0] ?? null;
  });

  /** Derived from the active role — single source of truth on refresh. */
  readonly portal = computed((): Portal => {
    const code = this.activeRole()?.code;
    return (code && ROLE_PORTAL_MAP[code]) || 'employee';
  });

  login(email: string, password: string): Observable<AuthUser> {
    const body: LoginRequest = { email, password };
    return this.api.post<LoginResponse>(API_PATHS.loginUser, body).pipe(
      map((response) => {
        const user = response.data;
        this.persistSession(user);
        const defaultRole = this.pickDefaultRole(user.roles);
        if (defaultRole) {
          this.setActiveRole(defaultRole);
        }
        return user;
      }),
      catchError((error: HttpErrorResponse) => {
        const message =
          (error.error as { message?: string } | null)?.message ??
          'Sign-in failed. Check your email and password.';
        return throwError(() => new Error(message));
      }),
    );
  }

  logout(): void {
    this.api
      .post<LogoutResponse>(API_PATHS.logOutUser, {})
      .pipe(finalize(() => this.clearLocalSession()))
      .subscribe();
  }

  /** The token is dead, so drop the session rather than try to log out of it. */
  expireSession(): void {
    // Parallel requests all fail at once; only the first should say so.
    if (!this.token()) {
      return;
    }

    this.clearLocalSession();
    this.toast.warning('Your session has expired. Please sign in again.', {
      title: 'Signed out',
    });
  }

  /** Switch active role from the header dropdown and navigate to its portal. */
  switchRole(roleId: number): void {
    const role = this.roles().find((item) => item.id === roleId);
    if (!role || role.id === this.activeRoleIdState()) {
      return;
    }
    this.setActiveRole(role);
    void this.router.navigateByUrl(this.homeUrl());
  }

  /** Default landing URL for the current portal. */
  homeUrl(): string {
    return this.portal() === 'admin' ? '/admin/dashboard' : '/employee/dashboard';
  }

  private clearLocalSession(): void {
    this.tokenService.clearToken();
    sessionStorage.removeItem(STORAGE_KEYS.user);
    sessionStorage.removeItem(STORAGE_KEYS.activeRoleId);
    localStorage.clear();
    this.token.set(null);
    this.userState.set(null);
    this.activeRoleIdState.set(null);
    void this.router.navigate(['/login']);
  }

  private setActiveRole(role: AuthRole): void {
    sessionStorage.setItem(STORAGE_KEYS.activeRoleId, String(role.id));
    this.activeRoleIdState.set(role.id);
  }

  private pickDefaultRole(roles: AuthRole[]): AuthRole | null {
    return roles.find((role) => role.code === 'ADMIN') ?? roles[0] ?? null;
  }

  private persistSession(user: AuthUser): void {
    this.tokenService.setToken(user.token);
    this.token.set(user.token);
    sessionStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
    this.userState.set(user);
  }

  private readUser(): AuthUser | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const raw = sessionStorage.getItem(STORAGE_KEYS.user);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }

  private readActiveRoleId(): number | null {
    const raw = sessionStorage.getItem(STORAGE_KEYS.activeRoleId);
    return raw ? Number(raw) : null;
  }
}
